import React, { useContext, useEffect, useState } from 'react';
import { getApolloContext } from '@apollo/client';
import { Box, CircularProgress, Dialog, DialogContent, Typography } from '@mui/material';
import { getApiBaseUrl } from '../apolloClient';
import { MAINTENANCE_EVENT, MAINTENANCE_MESSAGE, isMaintenanceBody } from '../utils/maintenance';

/** How often the notice checks whether the API is back. */
export const MAINTENANCE_POLL_MS = 20_000;

/**
 * Shown over the page while the API is in a maintenance window (the move to the
 * new database). It appears when a request gets the API's maintenance answer,
 * checks every 20 seconds, and goes by itself once the API answers normally,
 * reloading the page's data. There's nothing to click.
 */
const MaintenanceNotice: React.FC = () => {
  // Read from the context rather than useApolloClient(), which throws when
  // there's no ApolloProvider (some tests render the app shell without one).
  const { client } = useContext(getApolloContext());
  const [active, setActive] = useState(false);

  useEffect(() => {
    const show = () => setActive(true);
    window.addEventListener(MAINTENANCE_EVENT, show);
    return () => window.removeEventListener(MAINTENANCE_EVENT, show);
  }, []);

  useEffect(() => {
    if (!active) return undefined;
    let stopped = false;
    const check = async () => {
      try {
        const response = await fetch(`${getApiBaseUrl()}/health`, { cache: 'no-store' });
        const body = await response.json().catch(() => null);
        if (stopped || isMaintenanceBody(response.status, body)) return;
        // Any other answer means the window is over; real errors show as usual.
        setActive(false);
        client?.refetchQueries({ include: 'active' }).catch(() => undefined);
      } catch {
        // Offline or unreachable: try again at the next check.
      }
    };
    const timer = window.setInterval(check, MAINTENANCE_POLL_MS);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [active, client]);

  return (
    <Dialog open={active} disableEscapeKeyDown aria-describedby="maintenance-message" maxWidth="xs">
      <DialogContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1 }}>
          <CircularProgress size={28} aria-hidden />
          <Typography id="maintenance-message" role="status">
            {MAINTENANCE_MESSAGE}
          </Typography>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default MaintenanceNotice;
